import { Textarea } from '../ui/textarea';

/**
 * Shared AdditionalDetails component for wizard flows
 * Provides a consistent textarea with character counter for collecting optional details
 * 
 * @param {Object} props
 * @param {string} props.value - Current textarea value
 * @param {function} props.onChange - Change handler (receives new value string)
 * @param {string} [props.label="Additional Details (Optional)"] - Label text
 * @param {string} [props.placeholder] - Placeholder text
 * @param {number} [props.maxLength=1000] - Maximum character limit
 * @param {string} [props.error] - Error message to display
 * @param {boolean} [props.disabled=false] - Whether the textarea is disabled
 * @param {string} [props.testId="additional-details-textarea"] - Test ID for automation
 * @param {string} [props.minHeight="150px"] - Minimum height of textarea
 */
export const AdditionalDetails = ({
  value = '',
  onChange,
  label = 'Additional Details (Optional)',
  placeholder = 'Share any specific requirements, preferences, or unique project details...',
  maxLength = 1000,
  error,
  disabled = false,
  testId = 'additional-details-textarea',
  minHeight = '150px'
}) => {
  const currentLength = (value || '').length;
  const isAtLimit = currentLength >= maxLength;
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    // Enforce max length
    if (newValue.length <= maxLength) {
      onChange(newValue);
    }
  };
  
  return (
    <div className="space-y-2">
      {/* Label */}
      {label && (
        <label 
          htmlFor={testId}
          className="block text-sm font-medium text-[#1F2933]/80"
        >
          {label}
        </label>
      )}
      
      {/* Textarea with character counter */}
      <div className="relative">
        <Textarea
          id={testId}
          value={value || ''}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          className={`bg-gray-50 border-gray-200 text-[#1F2933] resize-none pr-16 ${
            error ? 'border-red-500' : ''
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ minHeight }}
          data-testid={testId}
        />
        
        {/* Character counter */}
        <span 
          className={`absolute bottom-3 right-3 text-xs ${
            isAtLimit ? 'text-red-500' : 'text-gray-400'
          }`}
        >
          {currentLength}/{maxLength}
        </span>
      </div>
      
      {/* Error message */}
      {error && (
        <p className="text-sm text-red-500 mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

export default AdditionalDetails;
